import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Delete, Upload } from '@factorialco/factorial-one-react/icons/app'
import {
  DeleteClassDocumentsDocument,
  TrainingClassDocumentsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import { FileItem, FilesList } from 'modules/trainings/components/Files/FilesList'

import { DetailOutletContext } from '../../../types'

export default function ClassDetailsDocumentsTab() {
  const { id, classId } = useStrictParams<'id' | 'classId'>()
  const { company } = useCurrentSession()
  const { canEdit } = useOutletContext<DetailOutletContext>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [deleteTrainingClassFile] = useMutation(DeleteClassDocumentsDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingClassFile,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingClassDocumentsDocument],
  })

  const source = useGraphqlDataSource({
    // TODO: We're fetching trough the training class because there's an id mismatch
    // between the training class and the training class file at the entity level
    document: TrainingClassDocumentsDocument,
    search: {
      enabled: true,
    },
    mapVariables: ({ search }) => ({
      id: Number(classId),
      companyId: company.id,
      search: search,
    }),
    itemUrl: (item) => {
      return navigation.trainings.list.details_revamp.view.trainingClasses.detail.documents.previewFile.fullPath(
        {
          id: Number(id),
          classId: Number(classId),
          classFileId: Number(item.id),
        }
      )
    },
    mapData: (data): FileItem[] => {
      return (
        data.trainings.trainingClass?.classFilesConnection.nodes.map((file) => ({
          contentType: file.contentType,
          filename: file.filename,
          id: file.id,
          size: file.size,
          title: file.title,
          type: 'trainingClass',
        })) || []
      )
    },
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingClass?.classFilesConnection.totalCount || 0,
      }
    },
    itemActions: (item: FileItem) => {
      if (!canEdit) return []
      return [
        {
          label: i18n.t('common.delete'),
          onClick: () => deleteTrainingClassFile({ variables: { id: item.id } }),
          icon: Delete,
        },
      ]
    },
    primaryActions: () => {
      if (!canEdit) return
      return {
        icon: Upload,
        label: i18n.t('common.upload'),
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.trainingClasses.detail.documents.newFile.fullPath(
              {
                id: Number(id),
                classId: Number(classId),
              }
            )
          )
        },
      }
    },
  })
  return (
    <Box gap="s16">
      <SectionHeader
        title={i18n.t('trainings.trainings_revamp.training_class.documents.section_header.title')}
        description={i18n.t(
          'trainings.trainings_revamp.training_class.documents.section_header.description'
        )}
        separator="bottom"
      />
      <FilesList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.trainings_revamp.training_class.documents.datacollection.empty_state.no_data.title'
            ),
            description: i18n.t(
              'trainings.trainings_revamp.training_class.documents.datacollection.empty_state.no_data.description'
            ),
          },
        }}
      />
      <Outlet />
    </Box>
  )
}
